import Model from "../Model";
import LegalAddress from "./LegalAddress";
import {uptime} from "os";

export type TypeCountryLegalInformation = {
    countryIso2: string,
    countryIso3: string,
    identifierGroups: LegalInformationIdentifierConfigGroup[],
    vats: number[]
}

export type LegalInformationIdentifierConfigGroup = {
    identifiers: LegalInformationIdentifierConfig[]
}

export type LegalInformationIdentifierConfig = {
    key:string,
    countryIso3:string,
    required:boolean,
    displayName:string,
    bestIdentifierForCountry:boolean,
}

export type LegalInformationIdentifier = {
    key:string,
    value:string,
    countryIso:string,
}

export default class LegalInformation extends Model {
    public uid!: Uuid;

    //(Numéro Interne de classement)
    private _NIC: string = "";
    private _siren: string = "";
    public vatNumber: string = "";
    public rcs!: string;
    public legalForm: string = 'SARL';
    public phoneNumber!: string;
    public email!: string;
    public lastname!: string;
    public firstname!: string;
    public companyName!: string;
    public beforeText!: string;
    public afterText!: string;
    public showAddressOpposition: boolean = false;
    public identifiers : LegalInformationIdentifier[] = [];

    private _isBillingAdresseSameAsAddress: boolean = true;
    get isBillingAdresseSameAsAddress(): boolean {
        return this._isBillingAdresseSameAsAddress;
    }

    set isBillingAdresseSameAsAddress(value: boolean) {
        this._isBillingAdresseSameAsAddress = value;
    }

    public address: LegalAddress|null = new LegalAddress();
    public billingAddress: LegalAddress = new LegalAddress();

    get NIC(): string {
        return this._NIC;
    }
    set NIC(value: string) {
        if (value.length > 5) {
            value = value.slice(0, 5);
        }
        this._NIC = value;
    }
    get siren(): string {
        return this._siren;
    }

    set siren(value: string) {
        if (value && value.length > 9) {
            value = value.slice(0, 9);
        }
        this._siren = value;
    }

    get siret(): string {
        return this._siren + this._NIC;
    }

    set siret(value: string) {
        let i: number = 0;
        if(value && value.length>9){
            this._NIC=value.substr(9,value.length-1);
        }
    }

    /**
     * Construct a product from a javascript object of a json string
     * @param obj either a javascript object or a json string
     */
    constructor(obj: any = {}) {
        super();
        if (typeof obj === 'string') {
            obj = JSON.parse(obj);
        }
        LegalInformation.buildFromJson(this, obj);
    }

    /**
     * builds the class instance from a javascript object
     * @param instance this
     * @param json javascript object
     */
    protected static buildFromJson(instance: any, json: any) {
        if (json) {
            for (let prop in json) {
                if (json.hasOwnProperty(prop)) {
                    if (prop === 'address' || prop === 'billingAddress') {
                        instance[prop] = new LegalAddress(json[prop]);
                    } else {
                        instance[prop] = json[prop];
                    }
                }
            }
        }
    }


    emptyValues() {
        LegalInformation.buildFromJson(this, {});
    }

    //TODO: Do this function
    exportToJson() {
        let JSON: any = {};
        JSON.uid = this.uid;
        JSON.legalForm = this.legalForm;
        JSON.phoneNumber = this.phoneNumber;
        JSON.email = this.email;
        JSON.lastname = this.lastname;
        JSON.firstname = this.firstname;
        JSON.companyName = this.companyName;
        JSON.beforeText = this.beforeText;
        JSON.afterText = this.afterText;
        JSON.identifiers = this.identifiers;

        if(this.address) {
            JSON.address = this.address.exportToJson();
            if (this._isBillingAdresseSameAsAddress) {
                JSON.billingAddress = this.address.exportToJson();
            } else {
                JSON.billingAddress = this.billingAddress.exportToJson();
            }
        } else {
            JSON.address = null;
            JSON.billingAddress = null;
            JSON.showAddressOpposition = true;
        }
        return JSON;
    }

    getVisibleName() : string{
        if(this.companyName) return this.companyName;
        return this.firstname+' '+this.lastname;
    }
    isPro(){
        return this.companyName !== null && this.companyName !== '';
    }

    fill(Updater: LegalInformation) {
        LegalInformation.buildFromJson(this, Updater);
    }

    set legalFormLongVersion(value: string) {
        this.legalForm = 'SARL';

        for(let v of LegalInformation.getLegalFormMap()){
            if(value === v.name){
                this.legalForm = v.name;
                break;
            }else if(value.startsWith(v.value)){
                this.legalForm = v.name;
                break;
            }
        }
    }

    public static getLegalFormMap() : Array<{ name: string, value: string }>{
        return [
            {name: 'EI', value: 'Entreprise individuelle (EI)'}
            , {name: 'EIRL', value: 'Entreprise individuelle à responsabilité limitée (EIRL)'}
            , {name: 'EURL', value: 'Entreprise unipersonnelle à responsabilité limitée (EURL)'}
            , {name: 'SARL', value: 'Société à responsabilité limitée (SARL)'}
            , {name: 'SA', value: 'Société anonyme (SA)'}
            , {name: 'SAS', value: 'Société par actions simplifiée (SAS)'}
            , {name: 'SASU', value: 'Société par actions simplifiée unipersonnelle (SASU)'}
            , {name: 'SNC', value: 'Société en nom collectif (SNC)'}
            , {name: 'SCOP', value: 'Société coopérative de production (SCOP)'}
            , {name: 'EPA', value: 'Établissements Publics à caractère Administratif'}
            , {name: 'EPA', value: 'Établissements Publics à caractère Administratif'}
            , {name: 'Association déclarée', value: 'Association déclarée'}
            , {name: 'RUP', value: "Association reconnue d'utilisaté publique"}
            , {name: 'Fondation', value: "Fondation"}
            , {name: 'EPS', value: "Etablissement public de santé"}
            , {name: 'EPA', value: "Etablissement public administratif"}
            , {name: 'Collectivité territoriale commune', value: "Collectivité territoriale commune"}
            , {name: 'EPIC', value: "Etablissement public à caractère industriel ou commercial"}
            , {name: 'EPLE', value: "Établissements publics locaux d'enseignement"}
            , {name: 'EPST', value: "Établissements publics à caractère scientifique et technologique"}
            , {name: 'EPSCP', value: "Établissements publics à caractère scientifique, culturel et professionnel"}
            , {name: 'EPCS', value: "Établissements publics de coopération scientifique"}
            , {name: 'EPCC', value: "Établissements publics de coopération culturelle"}
            , {name: 'EPCE', value: "Établissements publics de coopération environnementale"}
            , {name: 'EPCI', value: "Établissements publics de coopération intercommunale"}
            , {name: 'OPH', value: "Offices public de l'habitat"}
            , {name: 'SDIS', value: "Services départementaux d'incendie et de secours"}
            , {name: 'CCAS', value: "Centre communal d'action sociale"}
            , {name: 'SELARL', value: "Société d'Exercice Libéral A Responsabilité Limitée"}
        ];
    }

}
